import { ExclamationCircleOutlined } from "@ant-design/icons"
import { vendorRecommendations } from "@finway-group/shared/lib/consts"
import { supportedDocumentFormats } from "@finway-group/shared/lib/consts/common"
import { Vendor } from "@finway-group/shared/lib/models"
import { Button, Tag, Tooltip, Upload } from "antd"
import { RcFile } from "antd/lib/upload"
import { t } from "i18next"
import React, { useState } from "react"
import { Upload as UploadIcon } from "react-feather"
import { API_URL } from "~Shared/config/consts"
import { getIBANPrintFormat, getRecommendationTagColour, getTooltipPopupContainer, isCreditorNumberDATEVCompatible } from "~Shared/utils/helper.utils"

interface VendorBusinessDetailsProps {
    vendor: Vendor
    canEditContracts: boolean
    isUploading: boolean
    doFileUpload: (file: RcFile) => void
}

export const VendorBusinessDetails = ({ vendor, canEditContracts, isUploading, doFileUpload }: VendorBusinessDetailsProps) => {
    const [fileList] = useState<Array<any>>([])

    return (
        <>
            <ul className="description-list overflow-hidden">
                <li>
                    <span className="title min-w-100">{t("label:name")}</span>
                    <span className="text truncate">{vendor.name}</span>
                </li>
                <li>
                    <span className="title min-w-100">{t("label:description")}</span>
                    <span className="text truncate">{vendor.description || "-"}</span>
                </li>

                <li>
                    <span className="title min-w-100">{t("label:creditor_number")}</span>
                    <span className="text truncate">
                        {vendor.creditorNumber}
                        {vendor.creditorNumber && !isCreditorNumberDATEVCompatible(String(vendor.creditorNumber)) && (
                            <span className="relative" style={{ bottom: 3 }}>
                                <Tooltip getPopupContainer={getTooltipPopupContainer} title={t("tooltips:vendor.incompatible_creditor_number")} overlayStyle={{ width: "200px" }}>
                                    <ExclamationCircleOutlined className="ml-6" />
                                </Tooltip>
                            </span>
                        )}
                    </span>
                </li>
                <li>
                    <span className="title">{t("label:recommendation")}</span>
                    <span className="text">
                        <Tag className={`min-w-80" ${getRecommendationTagColour(vendorRecommendations[vendor.recommendation])}`}>
                            <span className="">{t(`label:recommendations.${vendorRecommendations[vendor.recommendation].toLowerCase()}`)}</span>
                        </Tag>
                    </span>
                </li>
                <li>
                    {/* TODO replace iban class w/ min-w-[20%] after upgrading to tailwind v3 https://tailwindcss.com/docs/min-width#arbitrary-values */}
                    <span className="title iban">{t("label:iban")}</span>
                    <span className="text-right">{vendor.iban ? getIBANPrintFormat(vendor.iban) : "-"}</span>
                </li>
                <li>
                    <span className="title">{t("label:bic")}</span>
                    <span className="text truncate max-w-200">{vendor.bic || "-"}</span>
                </li>
                <li>
                    <span className="title">{t("label:tax_number")}</span>
                    <span className="text truncate max-w-200">{vendor.taxNumber || "-"}</span>
                </li>
                <li>
                    <span className="title">{t("label:vat_number")}</span>
                    <span className="text truncate max-w-200">{vendor.vatNumber || "-"}</span>
                </li>

                {!vendor.deleted && (
                    <li>
                        <span className="grid-breakpoints hidden title xs:block xs:w-1/2 sm:hidden xl:block xl:w-1/2">{t("label:contracts")}</span>
                        <span className="grid-breakpoints w-full xs:w-1/2 sm:w-full xl:w-1/2 ant-btn p-0">
                            <span className="block">
                                <Upload
                                    className="custom-upload"
                                    action={`${API_URL}/files`}
                                    headers={{ Authorization: `Bearer ${localStorage.accessToken}` }}
                                    multiple
                                    listType="text"
                                    fileList={fileList}
                                    showUploadList={true}
                                    accept={supportedDocumentFormats.join(",")}
                                    customRequest={(req) => {
                                        doFileUpload(req.file as RcFile)
                                    }}
                                    disabled={!canEditContracts}
                                >
                                    <Button size="small" loading={isUploading} disabled={!canEditContracts} className="btn-default block w-full">
                                        <UploadIcon />
                                        <span>{t("action:upload_contract")}</span>
                                    </Button>
                                </Upload>
                            </span>
                        </span>
                    </li>
                )}
            </ul>
        </>
    )
}
